/**
 * Cart API client — typed wrappers around `/cart/*` endpoints used by the
 * customer-facing storefront.
 *
 * SF-CART-001 covers the full cart lifecycle — the
 * six thunk-targets the cart slice consumes: fetch, add, update, remove,
 * apply-coupon, remove-coupon. Each of those returns the full
 * server-authoritative {@link CartDTO} so the slice can replace state
 * wholesale rather than reconciling line-item diffs.
 *
 * Per `.claude/rules/frontend-data-flow.md` this module is the sole
 * gateway between the platform and the rest of the web stack — every
 * thunk that touches the cart endpoint MUST go through these helpers
 * (which carry the envelope-unwrap + ApiError translation contract).
 *
 * SF-ACCT-004, SF-CART-001.
 */
import { apiClient } from '@dfq/shared/api/client';
import {
  ApiError,
  throwAsApiError,
  unwrap,
  type ApiErrorSeverity,
} from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  CartDTO,
  CartItemDTO,
  CartItemFileReference,
  CartItemInput,
  CartItemSelectedOption,
  CartItemUpdate,
  ContourSettingsInput,
  CouponDTO,
} from '@dfq/shared/types/cart';

/**
 * Sizing-mode union accepted by the platform's cart-add endpoint. Mirrors
 * `OrderItemSizingMode` from `account.ts` 1:1 — kept inline so the cart
 * module can be consumed without importing account types.
 */
export type CartItemSizingMode =
  | 'vehicle_lookup'
  | 'custom_dimensions'
  | 'predefined_size'
  | 'fixed_flat';

/**
 * DELETE `/api/v1/cart/items/:id` — remove a single line item from the
 * authenticated customer's cart. Used by the SF-ACCT-004 reorder flow
 * when the customer cancels a partial-restore: we issue one delete per
 * successfully-added line so the cart returns to its pre-reorder state.
 *
 * 204 No Content responses unwrap to `{}` (the helper keeps the shape
 * symmetrical with the rest of the module). Errors surface as `ApiError`.
 */
export async function deleteCartItemApi(itemId: number): Promise<void> {
  try {
    await apiClient.delete<ApiEnvelope<Record<string, never>>>(
      `/cart/items/${itemId}`,
    );
  } catch (err) {
    throwAsApiError(err);
  }
}

// ----------------------------------------------------------------------------
// SF-CART-001 — full cart lifecycle helpers (cartApiClient).
//
// Six typed wrappers consumed by the cart slice (`@dfq/shared/stores/cart`):
//   - fetchCartApi          GET    /cart
//   - addCartItemApiV2      POST   /cart/items        (full cart returned)
//   - updateCartItemApi     PUT    /cart/items/:itemId
//   - removeCartItemApi     DELETE /cart/items/:itemId (returns full cart)
//   - applyCartCouponApi    POST   /cart/coupon
//   - removeCartCouponApi   DELETE /cart/coupon
//
// Each helper unwraps the `{ status, message, data }` envelope to a
// strict CartDTO from `@dfq/shared/types/cart` and rethrows any failure
// as a CartApiError so the slice's `rejectWithValue` path receives a
// structured error object (`message`, `severity`, field `errors`, `status`
// and a stable `error_code`) — never a raw axios error.
// ----------------------------------------------------------------------------

/**
 * Structured error thrown by the SF-CART-001 cart helpers. Wraps the
 * underlying ApiError's envelope fields with a guaranteed `error_code`
 * slot so the slice doesn't have to check for `undefined` before
 * reading the discriminator.
 */
export class CartApiError extends Error {
  public readonly error_code: string;

  public readonly status: number;

  public readonly errors: Record<string, string[]> | undefined;

  public readonly severity: ApiErrorSeverity | undefined;

  public readonly context: Record<string, unknown> | undefined;

  constructor(params: {
    message: string;
    error_code: string;
    status: number;
    errors?: Record<string, string[]>;
    severity?: ApiErrorSeverity;
    context?: Record<string, unknown>;
  }) {
    super(params.message);
    this.name = 'CartApiError';
    this.error_code = params.error_code;
    this.status = params.status;
    this.errors = params.errors;
    this.severity = params.severity;
    this.context = params.context;
  }
}

function fallbackCartErrorCode(status: number): string {
  if (status === 0) return 'NETWORK_ERROR';
  if (status >= 500) return 'HTTP_5XX';
  if (status >= 400) return 'HTTP_4XX';
  return 'UNKNOWN';
}

function toCartApiError(err: unknown): CartApiError {
  if (err instanceof CartApiError) return err;
  if (err instanceof ApiError) {
    return new CartApiError({
      message: err.message,
      error_code: err.errorCode ?? fallbackCartErrorCode(err.status),
      status: err.status,
      errors: err.errors,
      severity: err.severity,
      context: err.context,
    });
  }
  if (err instanceof Error) {
    return new CartApiError({
      message: err.message,
      error_code: 'NETWORK_ERROR',
      status: 0,
    });
  }
  return new CartApiError({
    message: 'Unknown cart API error',
    error_code: 'UNKNOWN',
    status: 0,
  });
}

/**
 * Raw `data.cart` shape emitted by `App\Http\Presenters\CartPresenter`.
 * Money fields arrive as dollar strings (`"685.10"`) plus integer-cent
 * mirrors; the projector below converts everything to the integer-cent
 * {@link CartDTO} the slice + views consume. Kept private — callers only
 * ever see the projected DTO.
 */
interface RawCartItem {
  id: number;
  merged_from_cart_item_id?: number | null;
  contour_job_id?: number | null;
  contour_preview_id?: number | null;
  contour_settings?: ContourSettingsInput | null;
  product_id: number;
  product_name: string | null;
  material_id: number | null;
  material_name: string | null;
  selected_options?: CartItemSelectedOption[];
  configuration_summary: string;
  sizing_mode: string;
  sizing_summary: string;
  vehicle_id: number | null;
  quantity_sqft: string;
  quantity: number;
  unit_price: string;
  line_total: string;
  pricing_unit?: string;
  add_ons: unknown[];
  file_references: CartItemFileReference[];
  thumbnail_url?: string | null;
  width_inches?: number | null;
  height_inches?: number | null;
}

interface RawCart {
  id: number | null;
  item_count: number;
  subtotal: string;
  subtotal_cents: number;
  discount_cents: number;
  total_cents: number;
  coupon: CouponDTO | null;
  promotions: unknown[];
  items: RawCartItem[];
}

/** Parse a server dollar string/number to integer cents. */
function dollarsToCents(value: string | number): number {
  return Math.round(Number(value) * 100);
}

function projectCartItem(raw: RawCartItem): CartItemDTO {
  return {
    id: raw.id,
    merged_from_cart_item_id: raw.merged_from_cart_item_id ?? null,
    contour_job_id: raw.contour_job_id ?? null,
    contour_preview_id: raw.contour_preview_id ?? null,
    contour_settings: raw.contour_settings ?? null,
    product_id: raw.product_id,
    product_name: raw.product_name ?? undefined,
    material_id: raw.material_id,
    material_name: raw.material_name ?? undefined,
    vehicle_id: raw.vehicle_id,
    selected_options: raw.selected_options ?? [],
    options_json: {},
    sizing_mode: raw.sizing_mode,
    dimensions_json: {},
    quantity_sqft: Number(raw.quantity_sqft),
    quantity: raw.quantity,
    pricing_unit: raw.pricing_unit === 'per_piece' ? 'per_piece' : 'per_sqft',
    unit_price: dollarsToCents(raw.unit_price),
    line_total: dollarsToCents(raw.line_total),
    add_ons_json: raw.add_ons ?? [],
    files: raw.file_references ?? [],
    thumbnail_url: raw.thumbnail_url ?? null,
    width_inches: raw.width_inches ?? null,
    height_inches: raw.height_inches ?? null,
  };
}

/**
 * Project the server's `data.cart` payload into the integer-cent
 * {@link CartDTO} the slice + views consume. The server emits
 * `total_cents` / `subtotal_cents` / `discount_cents` (not `total` /
 * `subtotal` / `discount_total`) and per-item dollar strings, so a bare
 * cast would leave `total` undefined → `$0.00`. This bridges that gap.
 */
function projectCart(raw: RawCart): CartDTO {
  return {
    id: raw.id as number,
    expires_at: null,
    items: (raw.items ?? []).map(projectCartItem),
    coupon: raw.coupon ?? null,
    subtotal: raw.subtotal_cents ?? 0,
    discount_total: raw.discount_cents ?? 0,
    shipping_estimate: null,
    tax: null,
    total: raw.total_cents ?? 0,
  };
}

/**
 * Run an axios call through the shared envelope-unwrap path and rethrow
 * any failure as a CartApiError. Centralises the mapping so each helper
 * stays a one-liner.
 *
 * Cart endpoints nest the payload under `data.cart` (e.g. `->withData(
 * 'cart', $presenter->present($cart))`), so the unwrapped `data` slot is
 * `{ cart: RawCart }`. The raw cart is projected to a {@link CartDTO}
 * (integer cents) before it leaves this module. QA-PURCHASE-013.
 */
async function callCartEndpoint(
  exec: () => Promise<{ data: ApiEnvelope<{ cart: RawCart }>; status: number }>,
): Promise<CartDTO> {
  let response: { data: ApiEnvelope<{ cart: RawCart }>; status: number };
  try {
    response = await exec();
  } catch (err) {
    // `throwAsApiError` always throws — wrap once to upgrade the
    // resulting ApiError into a CartApiError.
    try {
      throwAsApiError(err);
    } catch (apiErr) {
      throw toCartApiError(apiErr);
    }
    // Unreachable — `throwAsApiError` never returns.
    throw toCartApiError(err);
  }
  try {
    return projectCart(unwrap(response.data, response.status).cart);
  } catch (err) {
    throw toCartApiError(err);
  }
}

/**
 * `GET /api/v1/cart` — fetch the authenticated user's cart.
 */
export async function fetchCartApi(): Promise<CartDTO> {
  return callCartEndpoint(() =>
    apiClient.get<ApiEnvelope<{ cart: RawCart }>>('/cart'),
  );
}

/**
 * `POST /api/v1/cart/items` — add a configured line item. Returns the full
 * server-recalculated cart.
 */
export async function addCartItemApiV2(
  payload: CartItemInput,
): Promise<CartDTO> {
  return callCartEndpoint(() =>
    apiClient.post<ApiEnvelope<{ cart: RawCart }>>('/cart/items', payload),
  );
}

/**
 * `PUT /api/v1/cart/items/:itemId` — update a cart line item.
 */
export async function updateCartItemApi(
  itemId: number,
  payload: CartItemUpdate,
): Promise<CartDTO> {
  return callCartEndpoint(() =>
    apiClient.put<ApiEnvelope<{ cart: RawCart }>>(
      `/cart/items/${encodeURIComponent(String(itemId))}`,
      payload,
    ),
  );
}

/**
 * `DELETE /api/v1/cart/items/:itemId` — remove a cart line item.
 */
export async function removeCartItemApi(itemId: number): Promise<CartDTO> {
  return callCartEndpoint(() =>
    apiClient.delete<ApiEnvelope<{ cart: RawCart }>>(
      `/cart/items/${encodeURIComponent(String(itemId))}`,
    ),
  );
}

/**
 * `POST /api/v1/cart/coupon` — apply a coupon code.
 */
export async function applyCartCouponApi(code: string): Promise<CartDTO> {
  return callCartEndpoint(() =>
    apiClient.post<ApiEnvelope<{ cart: RawCart }>>('/cart/coupon', { code }),
  );
}

/**
 * `DELETE /api/v1/cart/coupon` — remove the currently applied coupon.
 */
export async function removeCartCouponApi(): Promise<CartDTO> {
  return callCartEndpoint(() =>
    apiClient.delete<ApiEnvelope<{ cart: RawCart }>>('/cart/coupon'),
  );
}

/**
 * `POST /api/v1/cart/items/{itemId}/artwork` — attach an owned artwork file to
 * a cart line. Resolves on 204; throws ApiError on failure.
 */
export async function attachArtworkToCartItem(
  itemId: number,
  artworkFileId: number,
): Promise<void> {
  try {
    await apiClient.post(
      `/cart/items/${encodeURIComponent(String(itemId))}/artwork`,
      { artwork_file_id: artworkFileId },
    );
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * Convenience grouping export so consumers can `import { cartApi }` if
 * they prefer the namespace style.
 */
export const cartApi = {
  // SF-ACCT-004 reorder flow
  deleteCartItemApi,
  // SF-CART-001 cart slice
  fetchCart: fetchCartApi,
  addItem: addCartItemApiV2,
  updateItem: updateCartItemApi,
  removeItem: removeCartItemApi,
  applyCoupon: applyCartCouponApi,
  removeCoupon: removeCartCouponApi,
  // deferred artwork upload
  attachArtworkToCartItem,
};

/**
 * Re-export of the canonical cart types for convenience — callers can
 * `import { CartDTO } from '@dfq/shared/api/cart'` without reaching for
 * the types module.
 */
export type {
  CartDTO,
  CartItemDTO,
  CartItemFileReference,
  CartItemInput,
  CartItemUpdate,
  CartError,
  CouponDTO,
} from '@dfq/shared/types/cart';
